import type { OpenT, OpenVotesT } from '@mentimeter/question-modules-types';
import { useOnce } from '@mentimeter/react-hooks';
import { useEffect } from 'react';
import { useIdentifiedTextResponses } from '@mentimeter/presentation-data-hooks/results/identified/text-responses';
import type { OpenEndedStaticContent } from '@core-api/presentation/types/response';
import { trackUser } from '@api/tracking/client';
import { usePresentationContext } from '../../legacy-context/presentationContext';
import { getVisualizationState } from './presentation/utils/getVisualizationState';
import { useOpenSlideState } from './presentation/utils/useOpenSlideState';
import { PresentationModule } from './PresentationModule';
import type { OpenEndedResponse } from './presentation/types';
import { OpenEndedPresentationContext } from './OpenEndedPresentationContext';
import { getNumberOfColumns } from './utils/getNumberOfColumns';

/**
 * OpenPresentationDataHandler
 */
export function OpenPresentationDataHandler() {
  const {
    useQuestion,
    useConfig,
    useResults,
    usePace,
    useTestVotes,
    useSeries,
    useCurrentSession,
  } = usePresentationContext();
  const { mode, enableAnimations, readonly } = useConfig();
  const rawResults = useResults<OpenT>();
  const { step } = usePace();
  const { testVotesShowing } = useTestVotes();
  const {
    slideAdminKey,
    images,

    title,
    staticContent,
    interactiveContents,
    responseVisibility,
  } = useQuestion();

  const interactiveContent = interactiveContents[0]!;

  const [{ shouldShowGroups }, setSlideState] = useOpenSlideState();
  const { isCurrentSessionActive } = useCurrentSession();

  const votes: OpenVotesT[] =
    rawResults.type === 'open' ? rawResults.votes : [];
  const numberOfVoters =
    rawResults.type === 'open' ? rawResults.voter_count : 0;

  const { participation_identity_mode, participation_identity_visibility } =
    useSeries();
  const hasIdentifiedResultsSettingOnAndVisible =
    participation_identity_mode === 'identified' &&
    participation_identity_visibility === 'everyone';

  const shouldFetchIdentifiedResponses =
    hasIdentifiedResultsSettingOnAndVisible && isCurrentSessionActive;

  const { data: identifiedResponses, isLoading: isLoadingIdentifiedResponses } =
    useIdentifiedTextResponses(slideAdminKey, shouldFetchIdentifiedResponses);

  const visualizationState = getVisualizationState({
    votingOnAnswers:
      interactiveContent.voteSettings?.voteOnResponsePolicy === 'allow-votes',
    step,
    mode,
    testVotesShowing,
    hideResults: responseVisibility === 'hidden',
    showGroups: !!shouldShowGroups,
    numberOfAnswers: votes.length,
  });

  function trackViewedSlideState(
    state: 'start-voting' | 'viewed-upvoting-results',
  ) {
    trackUser({
      event: 'Viewed slide state',
      properties: {
        questionType: 'open',
        state,
        context: shouldShowGroups
          ? 'grouped-responses'
          : 'individual-responses',
      },
    });
  }

  useOnce(visualizationState === 'start-voting', () =>
    trackViewedSlideState('start-voting'),
  );
  useOnce(visualizationState === 'view-results', () =>
    trackViewedSlideState('viewed-upvoting-results'),
  );
  useOnce(visualizationState === 'view-grouped-results', () =>
    trackViewedSlideState('viewed-upvoting-results'),
  );

  useEffect(() => {
    if (shouldShowGroups) {
      // reset the state when pacing through visualization states
      setSlideState({
        activeClusterId: null,
        activeResponseIndex: null,
      });
    }
  }, [visualizationState, setSlideState, shouldShowGroups]);

  if (shouldFetchIdentifiedResponses && isLoadingIdentifiedResponses) {
    return null;
  }

  // can happen in desktop experience for now but overall nice to have a fallback since we already have votes
  const shouldFallbackToVotes = identifiedResponses === undefined;

  const shouldShowIdentifiedResponses =
    shouldFetchIdentifiedResponses &&
    !testVotesShowing &&
    !shouldFallbackToVotes;

  const responses: OpenEndedResponse[] = shouldShowIdentifiedResponses
    ? (identifiedResponses.textResponses ?? []).map((identifiedResponse) => ({
        id: identifiedResponse.id,
        responseText: identifiedResponse.responseText,
        voteCount: identifiedResponse.voteCount,
        participantDisplayName:
          identifiedResponse.participant?.displayName ?? 'unknown',
      }))
    : votes.map((vote) => ({
        id: vote.id,
        responseText: vote.answer,
        voteCount: vote.upvotes,
        participantDisplayName: null,
      }));

  const visibleResponses = responseVisibility === 'hidden' ? [] : responses;

  const openEndedStaticContent = staticContent as OpenEndedStaticContent;

  const numberOfColumns = getNumberOfColumns({
    hasImages: images.length > 0,
    snapDirection: openEndedStaticContent.layout?.snapDirection ?? 'x',
    imageLayoutPosition: openEndedStaticContent.image?.imageLayoutPosition ?? {
      start: 0,
      end: 0,
    },
    gridColumns: openEndedStaticContent.layout?.gridColumns ?? 0,
  });

  return (
    <OpenEndedPresentationContext.Provider
      value={{
        title,
        styledTitle: openEndedStaticContent.styledTitle as Record<
          string,
          unknown
        >,
        label: openEndedStaticContent.label,
        description: interactiveContent.description,
        textSizeOffset: openEndedStaticContent.textSizeOffset,
        shouldAnimate: enableAnimations,
        isReadonly: readonly,
        mode,
        visualization:
          openEndedStaticContent.interactiveContentStyling?.visualization ??
          'speech_bubbles',
      }}
    >
      <PresentationModule
        visualizationState={visualizationState}
        responses={visibleResponses}
        numberOfVoters={numberOfVoters}
        isUpvotingEnabled={
          interactiveContent.voteSettings?.voteOnResponsePolicy ===
          'allow-votes'
        }
        shouldHideResults={responseVisibility === 'hidden'}
        mode={mode}
        numberOfColumns={numberOfColumns}
      />
    </OpenEndedPresentationContext.Provider>
  );
}
